// src/components/header/Header.tsx
import React, {useEffect, useMemo, useState, useRef} from 'react';
import { Button, Col, Flex, message, Row, Space, Drawer, Spin } from 'antd';
import { Layout, Menu, theme, Modal } from 'antd';
import { MenuOutlined, CrownOutlined } from '@ant-design/icons';
import { Outlet, useNavigate } from 'react-router-dom';
import AvatarDropdown from '../avatarDropdown/AvatarDropdown';
import styles from './MyHeader.module.css';
import SigninForm from "../signin/SigninForm";
import ResetPasswordForm from "../ResetPasswordForm";
import { GlobalConfig } from "../../config/GlobalConfig";
import { COURSE_BASE_ROUTE, MY_UPLOAD_RESOURCE_ROUTE } from "../../router/routePaths";
import SignupForm from "../signup/SignupForm";
import { useAppSelector, useAppDispatch } from "../../hooks";
import { fetchCourses } from "../../store/thunks/courseThunks";
import {convertCoursesToMenuItems} from "../../utils/utils";
import { Header } from 'antd/es/layout/layout';
import MemberComponent from "../member/MemberComponent";

const useResponsive = (setMobileMenuVisible: (visible: boolean) => void) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
            if (window.innerWidth > 768) {
                setMobileMenuVisible(false);
            }
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [setMobileMenuVisible]);
    
    return isMobile;
};

const MyHeader: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const isLogin = useAppSelector((state) => state.user.isLogin);
    const user = useAppSelector((state) => state.user);
    const courses = useAppSelector((state) => state.course.courses);
    const coursesError = useAppSelector((state) => state.course.error);
    const isLoading = useAppSelector((state) => state.course.loading);

    const [isLoginModalVisible, setIsLoginModalVisible] = useState(false);
    const [isRegisterModalVisible, setIsRegisterModalVisible] = useState(false);
    const [isResetPasswordModalVisible, setIsResetPasswordModalVisible] = useState(false);
    const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);
    const [isMemberModalVisible, setIsMemberModalVisible] = useState(false);
    const isMobile = useResponsive(setIsMobileMenuVisible);

    const hasRequestedCourses = useRef(false);

    useEffect(() => {
        if (courses.length === 0 && !isLoading && !hasRequestedCourses.current) {
            hasRequestedCourses.current = true;
            dispatch(fetchCourses());
        }
    }, [courses.length, isLoading]);

    useEffect(() => {
        if (coursesError) {
            message.error(coursesError);
        }
    }, [coursesError]);

    const onNavItemClick = (e) => {
        const courseId = e.key;
        if (courseId) {
            navigate(`${COURSE_BASE_ROUTE}${courseId}`);
        } else {
            message.error('未找到课程');
        }
    };

    const onMyUploadClick = () => {
        navigate(MY_UPLOAD_RESOURCE_ROUTE);
    };

    const showLoginModal = () => {
        setIsLoginModalVisible(true);
    };

    const showRegisterModal = () => {
        setIsRegisterModalVisible(true);
    };

    const handleCancelLogin = () => {
        setIsLoginModalVisible(false);
    };

    const handleCancelRegister = () => {
        setIsRegisterModalVisible(false);
    };

    const handleCancelResetPassword = () => {
        setIsResetPasswordModalVisible(false);
    };

    const onForgetPassword = () => {
        handleCancelLogin();
        setIsResetPasswordModalVisible(true);
    };

    const showMemberModal = () => {
        setIsMemberModalVisible(true);
    };

    const closeMemberModal = () => {
        setIsMemberModalVisible(false);
    };

    const menuItems = useMemo(() => convertCoursesToMenuItems(courses), [courses]);

    const toggleMobileMenu = () => {
        setIsMobileMenuVisible(!isMobileMenuVisible);
    };

    const handleMobileMenuClick = (e) => {
        onNavItemClick(e);
        setIsMobileMenuVisible(false);
    };

    return (
        <>
            <Header className={styles.headerContainer}>
                <Row className={styles.header} wrap={false} align="middle" justify="space-between">
                    <Col flex="auto">
                        <Flex align="center" style={{ width: '100%' }}>
                            {isMobile && (
                                <Button
                                    className={styles.menuButton}
                                    type="text"
                                    icon={<MenuOutlined />}
                                    onClick={toggleMobileMenu}
                                />
                            )}
                            <h2 className={styles.title} onClick={() => navigate('/')}>
                                {GlobalConfig.appName}
                            </h2>
                            {!isMobile && (
                                <div className={styles.desktopMenu}>
                                    <Spin spinning={isLoading}>
                                        <Menu
                                            style={{borderBottom: 'none'}}
                                            onClick={onNavItemClick}
                                            mode="horizontal"
                                            items={menuItems}
                                        />
                                    </Spin>
                                </div>
                            )}
                        </Flex>
                    </Col>
                    <Col>
                        <Flex gap='small' align='center' className={styles.rightbox}>
                            {isLogin ? (
                                <>
                                    {!user.isMember && (
                                        <Button
                                            type="primary"
                                            icon={<CrownOutlined />}
                                            onClick={showMemberModal}
                                            className={styles.memberButton}
                                        >
                                            会员
                                        </Button>
                                    )}
                                    {user.isMember && !isMobile && (
                                        <Button key='my-upload' type="link" onClick={onMyUploadClick}>
                                            我的文件
                                        </Button>
                                    )}
                                    <AvatarDropdown />
                                </>
                            ) : (
                                <>
                                    <Button type="primary" onClick={showLoginModal}>
                                        登录
                                    </Button>
                                    <Button type="primary" onClick={showRegisterModal}>
                                        注册
                                    </Button>
                                </>
                            )}
                        </Flex>
                    </Col>
                </Row>
            </Header>

            <Drawer
                placement="left"
                open={isMobileMenuVisible}
                onClose={() => setIsMobileMenuVisible(false)}
                title={null}
                width="80%"
            >
                <Menu
                    onClick={handleMobileMenuClick}
                    mode="inline"
                    items={menuItems}
                />
                {isLogin && user.isMember && (
                    <Menu mode="inline">
                        <Menu.Item key="my-upload" onClick={onMyUploadClick}>
                            我的文件
                        </Menu.Item>
                    </Menu>
                )}
            </Drawer>

            <Modal
                title={<div style={{ textAlign: 'center', fontSize: '20px' }}>登录</div>}
                centered
                open={isLoginModalVisible}
                footer={null}
                onCancel={handleCancelLogin}
                getContainer={false}
            >
                <SigninForm onClose={handleCancelLogin} onForgetPassword={onForgetPassword} />
            </Modal>
            <Modal
                title={<div style={{ textAlign: 'center', fontSize: '20px' }}>注册</div>}
                centered
                open={isRegisterModalVisible}
                footer={null}
                onCancel={handleCancelRegister}
                getContainer={false}
            >
                <SignupForm onClose={handleCancelRegister} />
            </Modal>
            <Modal
                title={<div style={{ textAlign: 'center', fontSize: '20px' }}>重置密码</div>}
                centered
                open={isResetPasswordModalVisible}
                footer={null}
                onCancel={handleCancelResetPassword}
                getContainer={false}
            >
                <ResetPasswordForm onClose={handleCancelResetPassword} />
            </Modal>
            <MemberComponent isVisible={isMemberModalVisible} onClose={closeMemberModal} />
        </>
    );
};

export default MyHeader;